import { CONNECTOR_CAPS } from "./capabilities";
import { byType as connByType } from "../Connectors/catalog";

// Flow entry triggers (the mandatory first element). The chosen trigger drives
// the application direction (inbound/outbound). More types come later
// (campaigns, integrations, etc.).
export const TRIGGERS = {
  inbound_call: {
    key: "inbound_call",
    title: "Ao receber chamada",
    desc: "Dispara quando uma chamada chega num número vinculado a esta automação.",
    color: "#1E7E34",
    direction: "inbound",
    fields: [
      { name: "inboundNumberId", label: "Número de telefone", type: "phone", required: true },
      {
        name: "techPrefix",
        label: "Tech prefix (opcional)",
        type: "text",
        helperText:
          "Dígitos que a operadora envia ANTES do número (ex.: 1010 → a chamada chega como 1010 + número). Só publique com prefixo se a operadora realmente entregar assim, senão a automação deixa de receber. O prefixo é descartado: o fluxo continua vendo o número limpo em steps.trigger.call.to.",
      },
      { name: "record", label: "Gravar chamada", type: "boolean", helperText: "Grava o áudio da chamada (armazenado com segurança). Avise o interlocutor — exigido por lei (LGPD)." },
      { name: "recordPublic", label: "Tornar a gravação pública (link)", type: "boolean", showIf: d => !!d.record, helperText: "Gera um link ASSINADO da gravação, acessível pela internet SEM login (para compartilhar). O link expira no tempo abaixo." },
      {
        name: "recordPublicTtl",
        label: "Validade do link público",
        type: "select",
        showIf: d => !!d.record && !!d.recordPublic,
        options: [
          { value: "1h", label: "1 hora" },
          { value: "24h", label: "24 horas" },
          { value: "7d", label: "7 dias" },
          { value: "30d", label: "30 dias" },
        ],
      },
    ],
  },
  webhook: {
    key: "webhook",
    title: "Webhook",
    desc: "Dispara quando um sistema externo chama uma URL (HTTP). Útil para originar chamadas.",
    color: "#5F3DC4",
    direction: "outbound",
    fields: [
      { name: "method", label: "Método", type: "select", options: ["POST", "GET"] },
      { name: "secret", label: "Token de verificação (opcional)", type: "text" },
      {
        name: "responseStatus",
        label: "Resposta",
        type: "select",
        options: [
          { value: "200", label: "200 OK" },
          { value: "201", label: "201 Created" },
          { value: "202", label: "202 Accepted" },
          { value: "204", label: "204 No Content" },
          { value: "400", label: "400 Bad Request" },
          { value: "401", label: "401 Unauthorized" },
          { value: "403", label: "403 Forbidden" },
          { value: "404", label: "404 Not Found" },
          { value: "422", label: "422 Unprocessable" },
          { value: "500", label: "500 Server Error" },
          { value: "flow", label: "Responder no fluxo (nó Resposta HTTP)" },
        ],
      },
    ],
  },
  scheduled: {
    key: "scheduled",
    title: "Agendado",
    desc: "Dispara em horários definidos (frequência + horário).",
    color: "#B26A00",
    direction: "outbound",
    fields: [{ name: "schedule", label: "Quando disparar", type: "schedule" }],
  },
  api_call: {
    key: "api_call",
    title: "Ao originar chamada via API",
    desc: "A chamada é iniciada pela API (POST /v1/calls); este fluxo roda quando ela é atendida.",
    color: "#0B7285",
    direction: "outbound",
    // a chamada já existe quando este fluxo roda (a API originou) — os nós de
    // chamada não devem ser bloqueados nem exigir um nó "Iniciar chamada".
    providesCall: true,
    fields: [
      { name: "record", label: "Gravar chamada", type: "boolean", helperText: "Grava o áudio da chamada (armazenado com segurança). Avise o interlocutor — exigido por lei (LGPD)." },
      { name: "recordPublic", label: "Tornar a gravação pública (link)", type: "boolean", showIf: d => !!d.record, helperText: "Gera um link ASSINADO da gravação, acessível pela internet SEM login (para compartilhar). O link expira no tempo abaixo." },
      {
        name: "recordPublicTtl",
        label: "Validade do link público",
        type: "select",
        showIf: d => !!d.record && !!d.recordPublic,
        options: [
          { value: "1h", label: "1 hora" },
          { value: "24h", label: "24 horas" },
          { value: "7d", label: "7 dias" },
          { value: "30d", label: "30 dias" },
        ],
      },
    ],
  },
  manual: {
    key: "manual",
    title: "Acionamento manual",
    desc: "Roda o fluxo publicado quando você clica em \"Executar\" no próprio gatilho. Útil para testar ou disparar sob demanda.",
    color: "#495057",
    direction: "outbound",
    manualRun: true, // mostra botão "Executar" no nó do gatilho
    fields: [],
  },
  // Gatilho por EVENTO DE CHAMADA: reage depois da ligação (a gravação ficou
  // pronta). Mesmo mecanismo dos eventos de conector (event.<fonte>.<capacidade>),
  // mas a fonte é a própria plataforma — disparo interno, sem URL /w manual.
  "event.call.recording_saved": {
    key: "event.call.recording_saved",
    title: "Ao concluir gravação",
    desc: "Dispara quando a gravação de uma chamada fica pronta — com o link e os dados da chamada já disponíveis.",
    color: "#0B7285",
    direction: "outbound",
    group: "connectors",
    webhookBased: true,
    nativeInbound: true, // evento interno da plataforma — sem URL /w manual
    fields: [
      {
        name: "sourceApplicationId",
        label: "Fluxo de origem",
        type: "application",
        helperText: "Só reage às gravações deste fluxo. Em branco = qualquer fluxo seu.",
      },
      {
        name: "minKb",
        label: "Tamanho mínimo (KB)",
        type: "number",
        helperText: "Ignora gravações menores que isso. Ex.: 10 para descartar gravações vazias.",
      },
      { name: "secret", label: "Token de verificação (opcional)", type: "text" },
    ],
    // schema de saída (steps.trigger.event.body.*) — o nó e o copiloto mapeiam as
    // variáveis sem o usuário adivinhar.
    out: {
      fields: [
        { path: "publicUrl", desc: "Link público da gravação (pronto para compartilhar)" },
        { path: "mb", desc: "Tamanho da gravação (MB)" },
        { path: "bytes", desc: "Tamanho da gravação (bytes)" },
        { path: "callSid", desc: "ID interno da chamada" },
        { path: "at", desc: "Data/hora em que a gravação ficou pronta (ISO)" },
        { path: "call.externalId", desc: "ID original da chamada (recebido na entrada)" },
        { path: "call.a", desc: "Número A (origem informada na entrada)" },
        { path: "call.b", desc: "Número B (destino informado na entrada)" },
        { path: "call.sentido", desc: "Sentido informado na entrada (entrada/saída)" },
        { path: "call.from", desc: "Quem originou a chamada" },
        { path: "call.to", desc: "Linha/destino da chamada" },
        { path: "call.direction", desc: "Direção (inbound/outbound)" },
        { path: "call.duration", desc: "Duração da chamada (s)" },
        { path: "call.endedAt", desc: "Data/hora em que a chamada terminou (ISO)" },
      ],
      sample: {
        callSid: "a1b2c3d4-...",
        path: "recordings/2026/a1b2c3d4.mp3",
        bytes: 184320,
        mb: 0.176,
        publicUrl: "https://app.exemplo.com/g/a1b2c3d4?...",
        publicExpiresAt: 1790000000000,
        at: "2026-10-07T11:22:33.000Z",
        call: { externalId: "318768780_133215969@...", a: "19935001002", b: "1930917198", sentido: "entrada", from: "+5531988887777", to: "+553130000000", direction: "inbound", duration: 42, status: "completed", endedAt: "2026-10-07T11:21:40.000Z" },
        sourceApplicationId: 49,
        runId: "7f1e...-run",
      },
    },
  },
  // Gatilho CONSUMIDOR: uma automação normal que roda a cada novo registro trazido
  // por um Coletor de dados. A config é só escolher qual coletor (collectorId). O
  // disparo por registro é feito pelo poller do coletor (runtime).
  data_collector: {
    key: "data_collector",
    title: "Coletor de dados",
    desc: "Dispara a cada novo registro trazido por um Coletor de dados (1 execução por registro).",
    color: "#2B8A3E",
    direction: "outbound",
    fields: [{ name: "collectorId", label: "Coletor", type: "collector", required: true }],
  },
  // "sabores" do canvas que NÃO são apps de chamada — ocultos do picker normal.
  // tool = função chamável por agente (entrada → retorno); agent = agente de voz.
  tool: {
    key: "tool",
    title: "Ferramenta para IA",
    desc: "Função chamável por um agente de IA (entrada → retorno). Só nós de backend.",
    color: "#5F5E5A",
    direction: "outbound",
    hidden: true,
    fields: [],
  },
  agent: {
    key: "agent",
    title: "Agente de IA",
    desc: "Agente de voz (persona + voz + ferramentas).",
    color: "#A61E4D",
    direction: "outbound",
    hidden: true,
    fields: [],
  },
  // Coletor de dados: fluxo que roda no intervalo do Polling, produz uma lista de
  // registros e devolve pelo nó "Retornar ao coletor" (campo sequencial = cursor).
  // O nó do gatilho É o "Polling" (frequência). Só nós de backend.
  collector: {
    key: "collector",
    title: "Polling",
    desc: "Verifica a fonte no intervalo definido e traz só os registros novos (por um campo sequencial). Só nós de backend.",
    color: "#2B8A3E",
    direction: "outbound",
    hidden: true,
    fields: [
      {
        name: "pollEvery",
        label: "Frequência",
        type: "select",
        required: true,
        options: [
          { value: "5m", label: "A cada 5 minutos" },
          { value: "15m", label: "A cada 15 minutos" },
          { value: "30m", label: "A cada 30 minutos" },
          { value: "1h", label: "A cada 1 hora" },
          { value: "6h", label: "A cada 6 horas" },
          { value: "12h", label: "A cada 12 horas" },
          { value: "24h", label: "A cada 24 horas" },
        ],
      },
      {
        name: "backfill",
        label: "Processar histórico (backfill)",
        type: "boolean",
        info:
          "Por padrão o coletor começa pelos registros MAIS RECENTES e, dali em diante, só pega o que for surgindo de novo — não varre o acervo antigo. Com isto ligado, ele começa pelos MAIS ANTIGOS e avança página a página, processando todo o histórico que bate no filtro (ex.: todas as contratações desde o 1º do mês) antes de passar a monitorar as novidades. Use para um 'backfill' único de um volume grande. Vale para fontes SQL.",
      },
      {
        name: "drain",
        label: "Drenar por paginação cheia",
        type: "boolean",
        info:
          "Normalmente o coletor traz uma página por intervalo do Polling. Com isto ligado, sempre que uma rodada volta uma página CHEIA (ainda há backlog) ele dispara a próxima página na hora — drenando rápido até sobrar uma página incompleta, e aí volta ao ritmo normal do intervalo. Combine com 'Processar histórico' para esvaziar um acervo grande sem esperar vários intervalos. Atenção: acelera os disparos do consumidor — se cada registro liga/chama uma API, use com cuidado.",
      },
    ],
  },
};

// rótulo curto da frequência do coletor (para o corpo do nó Polling)
export const POLL_EVERY_LABEL = {
  "5m": "a cada 5 min",
  "15m": "a cada 15 min",
  "30m": "a cada 30 min",
  "1h": "a cada 1h",
  "6h": "a cada 6h",
  "12h": "a cada 12h",
  "24h": "a cada 24h",
};

// Connector EVENT capabilities become webhook-based triggers (the app posts to
// /w/<uuid>). triggerType "event.<conn>.<cap>" reuses the webhook engine.
// conectores cujo inbound chega pela CALLBACK da própria conta (não por uma URL
// /w/<uuid> que o usuário cola em algum lugar). Para esses, não mostramos URL do
// webhook / Copiar / Testar evento — a conta selecionada já recebe sozinha.
const NATIVE_INBOUND = new Set(["gupshup", "whatsapp_qr"]);

Object.keys(CONNECTOR_CAPS).forEach(conn => {
  const cname = (connByType(conn) || {}).name || conn;
  const color = (connByType(conn) || {}).color || "#0B7285";
  const nativeInbound = NATIVE_INBOUND.has(conn);
  CONNECTOR_CAPS[conn]
    .filter(c => c.kind === "event")
    .forEach(c => {
      const key = `event.${conn}.${c.key}`;
      TRIGGERS[key] = {
        key,
        title: `${cname}: ${c.label}`,
        desc: nativeInbound
          ? `Dispara quando a conta ${cname} selecionada recebe este evento.`
          : `Dispara quando o ${cname} envia este evento (webhook de entrada).`,
        color,
        direction: "outbound",
        connector: conn,
        group: "connectors",
        webhookBased: true,
        nativeInbound, // inbound via callback da conta — sem URL /w manual
        // qual CONTA/conector deste tipo dispara (a conta pode ter N) — o dispatch
        // só roda este fluxo para a conta escolhida. Sem escolha = qualquer conta.
        // Em inbound nativo (Gupshup) não há "Token de verificação" (não é /w).
        fields: nativeInbound
          ? [{ name: "connectorId", label: `Conta (${cname})`, type: "connector", connectorType: conn }]
          : [
              { name: "connectorId", label: `Conta (${cname})`, type: "connector", connectorType: conn },
              { name: "secret", label: "Token de verificação (opcional)", type: "text" },
            ],
        // schema de saída do evento (campos do steps.trigger.event.body), quando
        // conhecido — o nó/copiloto mapeiam variáveis sem precisar de explicação
        ...(c.out ? { out: c.out } : {}),
        // guia de setup do lado da ferramenta (vira o "?" no nó do canvas)
        ...(c.setup ? { setup: c.setup } : {}),
      };
      // Interceptação (spec handoff-canais-agente §3.4): quando o número é
      // atendido por um AGENTE de IA, este fluxo de recebimento pode "roubar" a
      // mensagem por palavra-chave. Só faz sentido no recebimento do WhatsApp QR.
      if (conn === "whatsapp_qr" && c.key === "ao_receber_mensagem") {
        TRIGGERS[key].fields = [
          ...TRIGGERS[key].fields,
          {
            name: "interceptKeywords",
            label: "Interceptar por palavra-chave (opcional)",
            type: "text",
            placeholder: "menu, 2ª via, falar com humano",
            helperText:
              "Se o número for atendido por um agente de IA, este fluxo pega a mensagem quando ela contém uma destas palavras. Separe por vírgula. Vazio = não intercepta.",
          },
          {
            name: "interceptExclusive",
            label: "Captura exclusiva (só este fluxo trata a mensagem)",
            type: "boolean",
            helperText:
              "Quando a conexão tem mais de um fluxo de recebimento, só este roda — os outros não respondem. Com palavras-chave preenchidas, só captura as mensagens que tiverem uma delas.",
          },
        ];
      }
    });
});

export const triggerList = () => Object.keys(TRIGGERS).map(k => TRIGGERS[k]).filter(t => !t.hidden);

// webhook-based triggers get a public /w/<uuid> URL and are handled by the
// webhook engine (the native webhook + every connector event).
export const isWebhookBased = triggerType =>
  triggerType === "webhook" || (triggerType || "").startsWith("event.");

export const triggerDirection = triggerType => {
  const t = TRIGGERS[triggerType];
  return t ? t.direction : "inbound";
};

// resumo do PARÂMETRO-CHAVE do gatilho p/ o corpo do nó (não o rótulo de porta).
const WD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const cronShort = cfg => {
  const crons = (cfg.crons && cfg.crons.length ? cfg.crons : cfg.cron ? [cfg.cron] : []).map(c => String(c || "").trim());
  if (!crons.length) return "defina o horário";
  const parse = c => {
    const p = c.split(/\s+/);
    if (p.length !== 5 || !/^\d+$/.test(p[0]) || !/^\d+$/.test(p[1])) return null;
    const [m, h, dom, , dow] = p;
    return { time: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, dom, dow };
  };
  const parsed = crons.map(parse);
  if (parsed.some(x => !x)) return crons.join(" · ");
  const times = [...new Set(parsed.map(x => x.time))].sort().join(", ");
  const first = parsed[0];
  if (first.dom === "*" && first.dow === "*") return `todo dia às ${times}`;
  if (first.dow !== "*" && first.dom === "*") {
    const days = [...new Set(parsed.flatMap(x => x.dow.split(",").map(Number).filter(n => !isNaN(n))))].sort((a, b) => a - b);
    return `${days.map(d => WD[d % 7]).join(", ")} às ${times}`;
  }
  if (first.dom !== "*" && first.dow === "*") return `dia ${first.dom} de cada mês às ${times}`;
  return crons.join(" · ");
};

export const triggerSummary = (triggerType, data) => {
  const t = TRIGGERS[triggerType];
  if (!t) return "";
  const cfg = data || {};
  if (triggerType === "scheduled") return cfg.runAt ? "uma vez" : cronShort(cfg);
  if (triggerType === "collector") return POLL_EVERY_LABEL[cfg.pollEvery] || "defina a frequência";
  if (triggerType === "data_collector") return "novo registro do coletor";
  if (triggerType === "webhook") return `ao receber ${cfg.method || "POST"}`;
  if (triggerType === "inbound_call") return "ao receber chamada";
  if (triggerType === "api_call") return "iniciada pela API";
  if (String(triggerType).startsWith("event.")) return (t.title || "").replace(/^Ao /i, "ao ").toLowerCase();
  return (t.title || "").toLowerCase();
};

// true when the trigger guarantees a live call already exists when the flow runs
// (inbound: a chamada chegou; api_call: a API originou e atendeu). Usado pelo
// builder pra não bloquear/ exigir "Iniciar chamada".
export const triggerProvidesCall = triggerType => {
  const t = TRIGGERS[triggerType];
  return !!(t && (t.providesCall || t.direction === "inbound"));
};
